import { getReceptionSettings } from "@/core/admin/establishment-store";
import {
  getBillingDocumentByAdmission,
  isAdmissionConsultationPaid,
} from "@/core/billing/billing-documents/billing-documents-store";
import type { Patient } from "@/core/patients/types";
import { hasThirdPartyCoverage } from "@/core/patients/types";
import { resolveServiceFromDestination } from "@/core/queue/types";

/** Destination clinique (généraliste ou spécialiste) soumise au contrôle financier. */
export function requiresConsultationPaymentGate(destination: string): boolean {
  const d = destination.trim().toLowerCase();
  if (d.includes("hospital")) return false;
  if (d.includes("résultat") || d.includes("resultat")) return false;
  if (d.includes("soins infirm") || d.includes("constantes")) return false;
  const { serviceId } = resolveServiceFromDestination(destination);
  return serviceId === "clinical";
}

export function canOrientToClinicalConsultation(
  patient: Patient,
  admissionId: string,
): { allowed: boolean; reason?: string } {
  if (hasThirdPartyCoverage(patient)) {
    return { allowed: true };
  }
  if (isAdmissionConsultationPaid(admissionId)) {
    return { allowed: true };
  }
  if (getReceptionSettings().allowReceptionConsultationProforma) {
    const doc = getBillingDocumentByAdmission(admissionId);
    if (doc && (doc.status === "proforma" || doc.status === "at_cashier")) {
      return {
        allowed: false,
        reason:
          "Proforma émise — le patient doit régler à la caisse avant l'orientation vers la consultation.",
      };
    }
  }
  if (getReceptionSettings().allowReceptionConsultationProforma) {
    return {
      allowed: false,
      reason:
        "Patient non assuré — émettez une proforma consultation à l'accueil ou orientez vers la caisse.",
    };
  }
  return {
    allowed: false,
    reason:
      "Consultation payante — prise en charge tiers payant ou règlement caisse requis.",
  };
}

/** Destination clinique payante non sélectionnable à l'accueil. */
export function isPaidClinicalDestinationDisabled(
  destination: string,
  patient: Patient | null,
  admissionId: string,
): boolean {
  if (!requiresConsultationPaymentGate(destination)) return false;
  if (!patient) return true;
  return !canOrientToClinicalConsultation(patient, admissionId).allowed;
}

export function filterOrientationDestinations(destinations: string[]): string[] {
  return destinations.filter((d) => !/comptabilit/i.test(d.trim()));
}
